import api from './api';
import { MenuItemFaqPayload } from '../types/menu';
import { ScanItemInfo, ScanItemRef, ScanLine, ScanResult } from '../components/scan/scanTypes';

/** The server's error message, or a fallback. */
export function scanErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string } }; code?: string };
  if (e?.response?.data?.error) return e.response.data.error;
  if (e?.code === 'ECONNABORTED') return 'That took too long. Check the signal and try again.';
  if (e?.code === 'ERR_NETWORK') return "Couldn't reach the server. Check the signal and try again.";
  return fallback;
}

/** What Jev keeps: true, anything a diner could order; 'beer', just the beer. Every other line comes back `dropped`. */
export type ScanClean = boolean | 'beer';
const cleanParam = (clean: ScanClean) => (clean ? { clean: clean === true ? 1 : clean } : {});

/** Read a menu photo and look its lines up in the shared scan menu. `save` also keeps the photo and any new dishes there. */
export async function scanPhoto(photo: Blob, matchItems = true, clean: ScanClean = false, save = false): Promise<ScanResult> {
  const form = new FormData();
  form.append('photo', photo, 'menu.jpg');
  const { data } = await api.post<ScanResult>('/scan', form, { timeout: 60000, params: { ...(matchItems ? {} : { match: 0 }), ...cleanParam(clean), ...(save ? { save: 1 } : {}) } });
  return data;
}

/** Match lines that were read earlier (the bundled samples). */
export async function scanLines(lines: Omit<ScanLine, 'match'>[], engine?: string, matchItems = true, clean: ScanClean = false): Promise<ScanResult> {
  const { data } = await api.post<ScanResult>('/scan/lines', { lines, engine }, { params: { ...(matchItems ? {} : { match: 0 }), ...cleanParam(clean) } });
  return data;
}

export async function getKnownItem(id: string): Promise<ScanItemInfo> {
  const { data } = await api.get<ScanItemInfo>(`/scan/items/${id}`);
  return data;
}

/** Ask the AI what a tapped line is. Only ever called on a tap. */
/** `itemId`, when the line is stored, keeps the explanation on that item. */
export async function analyzeLine(text: string, nearby: string[], menuSample: string[], itemId?: string): Promise<ScanItemInfo> {
  const { data } = await api.post<ScanItemInfo>('/scan/analyze', { text, nearby, menuSample, itemId }, { timeout: 40000 });
  return data;
}

/** FAQ entries for an item: stored ones for a known item, otherwise generated like a menu's. */
export async function getItemFaq(ref: ScanItemRef, menuSample: string[]): Promise<MenuItemFaqPayload> {
  const { data } = await api.post<MenuItemFaqPayload>('/scan/faq', { ...ref, menuSample }, { timeout: 60000 });
  return data;
}

/** Answer a diner's question about an item, the way the menu view does. */
export async function askAboutItem(ref: ScanItemRef, question: string): Promise<string> {
  const { data } = await api.post<{ answer: string }>('/scan/ask', { ...ref, question }, { timeout: 60000 });
  return data.answer;
}

/** English name and a short description, for a line a guide matched just part of. */
export async function describePartial(text: string, nearby: string[], menuSample: string[]): Promise<{ nameEn: string; description: string }> {
  const { data } = await api.post<{ nameEn: string; description: string }>('/scan/name', { text, nearby, menuSample }, { timeout: 20000 });
  return data;
}
